import React from 'react'
import { useTranslation } from 'react-i18next'

import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import LoadingButton from '@mui/lab/LoadingButton'

import { post } from '../../utils/service'
import Alert from '../alert/alert'
import { Log } from '../../types/log'
import { getFullscreenElement } from '../../utils/graphUtils'
import TimeField from '../profileEditor/timeField'

interface EditEventsDialogProps {
  log: Log
  maxTime: number
  onClose: () => void
  onSaveSuccess: (newLogData: Log) => void
}

const EditEventsDialog = (props: EditEventsDialogProps): JSX.Element => {
  const { t } = useTranslation()

  const [yellowingTimestamp, setYellowingTimestamp] = React.useState<number>(props.log.dryend_event_msec || 0)
  const [firstcrackTimestamp, setFirstcrackTimestamp] = React.useState<number>(props.log.firstcrack_event_msec || 0)
  const [isSaving, setIsSaving] = React.useState<boolean>(false)
  const [hasSaveError, setHasSaveError] = React.useState<boolean>(false)

  const isValidTimestamps = (yellowingMsec: number, firstcrackMsec: number): boolean => {
    return !!yellowingMsec && !!firstcrackMsec && yellowingMsec < firstcrackMsec
  }

  const saveEvents = (newYellowingTimestamp: number, newFirstcrackTimestamp: number): void => {
    if (isValidTimestamps(newYellowingTimestamp, newFirstcrackTimestamp)) {
      setHasSaveError(false)
      setIsSaving(true)
      const saveData = {
        dryend_event_msec: newYellowingTimestamp,
        firstcrack_event_msec: newFirstcrackTimestamp,
      }
      post<{ dryend_event_msec: number; firstcrack_event_msec: number }, Log>(`${props.log.url}events/`, saveData)
        .then((response) => {
          props.onSaveSuccess(response.data)
        })
        .catch(() => {
          setHasSaveError(true)
          setIsSaving(false)
        })
    }
  }

  return (
    <Dialog open={true} onClose={props.onClose} container={getFullscreenElement() || document.body}>
      <DialogContent>
        <Stack sx={{ width: 300 }} spacing={2}>
          <Stack direction="row">
            <TimeField
              autoFocus={true}
              timeValue={yellowingTimestamp}
              minValue={0}
              maxValue={props.maxTime}
              label={t('chartevent.dryend')}
              onBlur={(newValue: number) => setYellowingTimestamp(newValue)}
              onEnter={(newValue: number) => saveEvents(newValue, firstcrackTimestamp)}
            />
            <TimeField
              timeValue={firstcrackTimestamp}
              minValue={0}
              maxValue={props.maxTime}
              label={t('chartevent.firstcrack')}
              onBlur={(newValue: number) => setFirstcrackTimestamp(newValue)}
              onEnter={(newValue: number) => saveEvents(yellowingTimestamp, newValue)}
            />
          </Stack>
          {hasSaveError && <Alert severity="error">{t('logview.moveeventserror')}</Alert>}
          {yellowingTimestamp > firstcrackTimestamp && (
            <Alert severity="error">{t('logview.moveeventsfcbeforeyellowing')}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>{t('common.cancel')}</Button>
        <LoadingButton
          variant="contained"
          loading={isSaving}
          disabled={!isValidTimestamps(yellowingTimestamp, firstcrackTimestamp)}
          onClick={() => saveEvents(yellowingTimestamp, firstcrackTimestamp)}
        >
          {t('logview.moveevents')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default EditEventsDialog
